import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import Slider from "react-rangeslider";
import Select, { components } from "react-select";
import Draggable from "react-draggable";
import { Label, Collapse as BsCollapse } from "reactstrap";
import { env } from "configs/EnvironmentConfig";
import { API_BASE_URL } from "configs/AppConfig";

import ColorPicker from "../ColorPicker";
import Collapse from "../Collapse";
import { viewerConfig } from "../const";
import { InvoiceTemplateContext } from "../InvoiceTemplateContext";
import FileSelector from "../FileSelector";

const Checkbox = props => {
  const [checked, setChecked] = useState(() => {
    return props.checked;
  });

  const handleChange = () => {
    setChecked(!checked);
    props.onChange?.();
  };

  return (
    <div className="form-check form-switch form-switch-md ms-auto">
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        onChange={handleChange}
        onBlur={props.onBlur}
      />
    </div>
  );
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

const useStyleWidth = (data, key) => {
  const [value, setValue] = useState(() => {
    const widthStyle = get(data, `${key}.style.width`);
    const width = widthStyle.substring(0, widthStyle.length - 2);
    return parseInt(width, 10);
  });

  return [value, setValue];
};

const useStyleOpacity = (data, key) => {
  const [value, setValue] = useState(() => {
    const opacity = get(data, `${key}.style.opacity`);
    return parseFloat(opacity) * 100;
  });

  return [value, setValue];
};

const borderStyleOptions = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "double", label: "Double" },
];

const borderWidthOptions = [
  { value: "0px", label: "0px" },
  { value: "1px", label: "1px" },
  { value: "2px", label: "2px" },
  { value: "3px", label: "3px" },
  { value: "4px", label: "4px" },
  { value: "5px", label: "5px" },
];

const backgroundImages = [
  `${location.origin}/images/invoice-backgrounds/1.png`,
  `${location.origin}/images/invoice-backgrounds/2.png`,
  `${location.origin}/images/invoice-backgrounds/3.png`,
  `${location.origin}/images/invoice-backgrounds/4.png`,
  `${location.origin}/images/invoice-backgrounds/5.png`,
  `${location.origin}/images/invoice-backgrounds/6.png`,
  `${location.origin}/images/invoice-backgrounds/7.png`,
  `${location.origin}/images/invoice-backgrounds/8.png`,
  `${location.origin}/images/invoice-backgrounds/9.png`,
  `${location.origin}/images/invoice-backgrounds/10.png`,
  `${location.origin}/images/invoice-backgrounds/11.png`,
  `${location.origin}/images/invoice-backgrounds/12.png`,
  `${location.origin}/images/invoice-backgrounds/13.png`,
  `${location.origin}/images/invoice-backgrounds/14.png`,
  `${location.origin}/images/invoice-backgrounds/15.png`,
  `${location.origin}/images/invoice-backgrounds/16.png`,
];

const borderImages = [
  `${location.origin}/images/invoice-frames/1.png`,
  `${location.origin}/images/invoice-frames/2.png`,
  `${location.origin}/images/invoice-frames/3.png`,
  `${location.origin}/images/invoice-frames/4.png`,
  `${location.origin}/images/invoice-frames/5.png`,
  `${location.origin}/images/invoice-frames/6.png`,
  `${location.origin}/images/invoice-frames/7.png`,
  `${location.origin}/images/invoice-frames/8.png`,
];

const BorderStyleOption = props => {
  const data = props.data;

  return (
    <components.Option {...props}>
      <div
        style={{
          borderTop: `3px ${data.value} #000`,
          width: "120px",
          margin: "8px 0",
        }}
      ></div>
    </components.Option>
  );
};

BorderStyleOption.propTypes = {
  data: PropTypes.object,
};

const getDownloadImageUrl = () => {
  return `${API_BASE_URL}${env.API_MSP}/resources/download/`;
};

const InvoiceTemplateImageEditor = () => {
  const [watermarkRatio, setWatermarkRatio] = useState(1);
  const [watermarkThumbSize, setWatermarkThumbSize] = useState({
    width: "0px",
    height: "0px",
  });
  const { state, dispatch } = useContext(InvoiceTemplateContext);
  const { invoiceData, watermarkImageSize = { width: 0, height: 0 } } = state;

  const [qrCodeWidth, setQRCodeWidth] = useStyleWidth(invoiceData, "qrCode");
  const [logoWidth, setLogoWidth] = useStyleWidth(invoiceData, "logo");
  const [watermarkWidth, setWatermarkWidth] = useStyleWidth(
    invoiceData,
    "watermark"
  );
  const [backgroundWidth, setBackgroundWidth] = useStyleWidth(
    invoiceData,
    "background"
  );
  const [watermarkOpacity, setWatermarkOpacity] = useStyleOpacity(
    invoiceData,
    "watermark"
  );
  const [backgroundOpacity, setBackgroundOpacity] = useStyleOpacity(
    invoiceData,
    "background"
  );
  const [framePatternOpacity, setFramePatternOpacity] = useStyleOpacity(
    invoiceData,
    "frame.pattern"
  );

  const updateInvoiceData = (key, value) => {
    dispatch({
      type: "updateInvoiceData",
      payload: { path: key, value },
    });
  };

  const handleDragWatermark = e => {
    const transformStyle = e.srcElement.style.transform;
    const translate = transformStyle
      .substring(transformStyle.indexOf("(") + 1, transformStyle.indexOf(")"))
      .split(",");

    let translateX = null;
    let translateY = null;
    if (translate[0]) {
      translateX = translate[0].trim().substring(0, translate[0].indexOf("px"));
    }

    if (translate[1]) {
      translateY = translate[1]
        .trim()
        .substring(0, translate[1].indexOf("px") - 1);
    }

    updateInvoiceData("watermark.style", {
      transform: `translate(${translateX * 2.25}px, ${translateY * 2.25}px)`,
    });
  };

  const handleDragBackground = e => {
    const transformStyle = e.srcElement.style.transform;
    const translate = transformStyle
      .substring(transformStyle.indexOf("(") + 1, transformStyle.indexOf(")"))
      .split(",");

    let translateX = null;
    let translateY = null;
    if (translate[0]) {
      translateX = translate[0].trim().substring(0, translate[0].indexOf("px"));
    }

    if (translate[1]) {
      translateY = translate[1]
        .trim()
        .substring(0, translate[1].indexOf("px") - 1);
    }

    updateInvoiceData("background.style", {
      transform: `translate(${translateX * 2.25}px, ${translateY * 2.25}px)`,
    });
  };

  const readFile = event => {
    return new Promise((resolve, reject) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.onload = () => {
          const base64String = reader.result
            .replace("data:", "")
            .replace(/^.+,/, "");

          const base64 = `data:image/*;base64,${base64String}`;

          resolve({
            file,
            base64,
          });
        };
        reader.readAsDataURL(file);
      } else {
        reject(null);
      }
    });
  };

  const handleUploadLogo = event => {
    readFile(event).then(data => {
      updateInvoiceData("logo.url", data.base64);
    });
  };

  const handleUploadWatermark = event => {
    readFile(event).then(data => {
      updateInvoiceData("watermark.url", data.base64);
    });
  };

  const handleUploadBackground = event => {
    readFile(event).then(data => {
      updateInvoiceData("background.url", data.base64);
    });
  };

  useEffect(() => {
    if (watermarkImageSize) {
      setWatermarkRatio(watermarkImageSize.width / watermarkImageSize.height);
    }
  }, [watermarkImageSize]);

  useEffect(() => {
    const widthStyle = invoiceData.watermark.style.width;
    const width = widthStyle.substring(0, widthStyle.length - 2);
    setWatermarkThumbSize({
      width: `${width / 2.25}px`,
      height: `${width / 2.25 / watermarkRatio}px`,
    });
  }, [watermarkRatio, invoiceData.watermark.style]);

  return (
    <div>
      {/* Start QR Code */}
      <Collapse
        title="Sử dụng QR Code"
        canOpen={invoiceData.qrCode.enabled}
        header={
          <Checkbox
            checked={invoiceData.qrCode.enabled}
            onChange={() =>
              updateInvoiceData("qrCode.enabled", !invoiceData.qrCode.enabled)
            }
          />
        }
      >
        <Label>Tùy chỉnh kích thước QR Code</Label>
        <Slider
          value={qrCodeWidth}
          min={0}
          max={viewerConfig.qrCodeMaxWidth}
          orientation="horizontal"
          onChange={value => {
            setQRCodeWidth(value);
            updateInvoiceData("qrCode.style", { width: `${value}px` });
          }}
        />
      </Collapse>
      {/* End QR Code */}

      {/* Start Logo */}
      <Collapse
        title="Sử dụng Logo"
        canOpen={invoiceData.logo.enabled}
        header={
          <Checkbox
            checked={invoiceData.logo.enabled}
            onChange={() =>
              updateInvoiceData("logo.enabled", !invoiceData.logo.enabled)
            }
          />
        }
      >
        <div>
          <FileSelector onChange={handleUploadLogo} />
        </div>

        <div className="mt-4">
          <Label>Tùy chỉnh kích thước Logo</Label>
          <Slider
            value={logoWidth}
            min={0}
            max={viewerConfig.logoMaxWidth}
            orientation="horizontal"
            onChange={value => {
              setLogoWidth(value);
              updateInvoiceData("logo.style", { width: `${value}px` });
            }}
          />
        </div>
      </Collapse>
      {/* End Logo */}

      {/* Start watermark */}
      <Collapse
        title="Sử dụng Logo chìm"
        canOpen={invoiceData.watermark.enabled}
        header={
          <Checkbox
            checked={invoiceData.watermark.enabled}
            onChange={() =>
              updateInvoiceData(
                "watermark.enabled",
                !invoiceData.watermark.enabled
              )
            }
          />
        }
      >
        <div className="d-flex justify-content-start">
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="radio"
              name="useLogoForWatermark"
              id="watermark-radio-1"
              value="useLogo"
              checked={invoiceData.watermark.useLogo}
              onChange={() => updateInvoiceData("watermark.useLogo", true)}
            />
            <label className="form-check-label" htmlFor="watermark-radio-1">
              Sử dụng Logo
            </label>
          </div>
          <div className="form-check mb-3 ms-5">
            <input
              className="form-check-input"
              type="radio"
              name="useLogoForWatermark"
              id="watermark-radio-2"
              value="upload"
              checked={!invoiceData.watermark.useLogo}
              onChange={() => updateInvoiceData("watermark.useLogo", false)}
            />
            <label className="form-check-label" htmlFor="watermark-radio-2">
              Tải lên
            </label>
          </div>
        </div>

        <BsCollapse isOpen={!invoiceData.watermark.useLogo}>
          <div className="mb-4">
            <FileSelector onChange={handleUploadWatermark} />
          </div>
        </BsCollapse>

        <div>
          <Label>Tùy chỉnh kích thước</Label>
          <Slider
            value={watermarkWidth}
            min={0}
            max={viewerConfig.watermarkMaxWidth}
            orientation="horizontal"
            onChange={value => {
              setWatermarkWidth(value);
              updateInvoiceData("watermark.style", { width: `${value}px` });
            }}
          />
        </div>

        <div>
          <Label>Độ mờ</Label>
          <Slider
            value={watermarkOpacity}
            min={0}
            max={100}
            orientation="horizontal"
            onChange={value => {
              setWatermarkOpacity(value);
              updateInvoiceData("watermark.style", {
                opacity: `${value / 100}`,
              });
            }}
          />
        </div>

        <div>
          <Label>Tùy chỉnh vị trí</Label>
          <div className="watermark-draggable-container">
            <Draggable bounds="parent" onDrag={handleDragWatermark}>
              <div
                className="watermark-thumb"
                style={{
                  width: watermarkThumbSize.width,
                  height: watermarkThumbSize.height,
                  backgroundImage: `url(${
                    invoiceData.watermark.useLogo
                      ? invoiceData.logo.url
                      : invoiceData.watermark.url
                  })`,
                }}
              />
            </Draggable>
          </div>
        </div>
      </Collapse>
      {/* End watermark */}

      {/* Start background */}
      <Collapse
        title="Sử dụng hình nền"
        canOpen={invoiceData.background.enabled}
        header={
          <Checkbox
            checked={invoiceData.background.enabled}
            onChange={() =>
              updateInvoiceData(
                "background.enabled",
                !invoiceData.background.enabled
              )
            }
          />
        }
      >
        <div className="d-flex justify-content-start">
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="radio"
              name="backgroundSource"
              id="background-radio-1"
              checked={invoiceData.background.source === "library"}
              onChange={() => updateInvoiceData("background.source", "library")}
            />
            <label className="form-check-label" htmlFor="background-radio-1">
              Chọn ảnh nền từ thư viện
            </label>
          </div>

          <div className="form-check mb-3 ms-5">
            <input
              className="form-check-input"
              type="radio"
              name="backgroundSource"
              id="background-radio-2"
              checked={invoiceData.background.source === "upload"}
              onChange={() => updateInvoiceData("background.source", "upload")}
            />
            <label className="form-check-label" htmlFor="background-radio-2">
              Tải lên
            </label>
          </div>
        </div>

        <BsCollapse isOpen={invoiceData.background.source === "upload"}>
          <div className="mt-2">
            <FileSelector onChange={handleUploadBackground} />
          </div>
        </BsCollapse>

        <BsCollapse isOpen={invoiceData.background.source === "library"}>
          <div className="mt-3 row">
            {backgroundImages.map((url, index) => (
              <div key={index} className="col-3">
                <div
                  className={`border card border-primary bg-image-thumb mb-3 ${
                    invoiceData.background.url === url
                      ? "border-primary"
                      : "border-secondary"
                  }`}
                  onClick={() => {
                    updateInvoiceData("background.url", url);
                  }}
                >
                  <img src={url} />
                </div>
              </div>
            ))}
          </div>
        </BsCollapse>

        <div className="mt-3">
          <Label>Tùy chỉnh kích thước</Label>
          <Slider
            value={backgroundWidth}
            min={0}
            max={viewerConfig.backgroundMaxWidth}
            orientation="horizontal"
            onChange={value => {
              setBackgroundWidth(value);
              updateInvoiceData("background.style", { width: `${value}px` });
            }}
          />
        </div>

        <div>
          <Label>Độ mờ</Label>
          <Slider
            value={backgroundOpacity}
            min={0}
            max={100}
            orientation="horizontal"
            onChange={value => {
              setBackgroundOpacity(value);
              updateInvoiceData("background.style", {
                opacity: `${value / 100}`,
              });
            }}
          />
        </div>

        <div>
          <Label>Tùy chỉnh vị trí</Label>
          <div className="bg-draggable-container">
            <Draggable bounds="parent" onDrag={handleDragBackground}>
              <div
                className="bg-draggable-thumb"
                style={{
                  width: watermarkThumbSize.width,
                  height: watermarkThumbSize.height,
                  backgroundImage: `url(${invoiceData.background.url})`,
                }}
              />
            </Draggable>
          </div>
        </div>
      </Collapse>
      {/* End background */}

      {/* Start frame */}
      <Collapse
        title="Sử dụng khung viền"
        canOpen={invoiceData.frame.enabled}
        header={
          <Checkbox
            checked={invoiceData.frame.enabled}
            onChange={() =>
              updateInvoiceData("frame.enabled", !invoiceData.frame.enabled)
            }
          />
        }
      >
        <div className="d-flex justify-content-start">
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="radio"
              name="frameType"
              id="frame-radio-1"
              value="border"
              checked={invoiceData.frame.type === "border"}
              onChange={event =>
                updateInvoiceData("frame.type", event.target.value)
              }
            />
            <label className="form-check-label" htmlFor="frame-radio-1">
              Khung viền kẻ
            </label>
          </div>
          <div className="form-check mb-3 ms-5">
            <input
              className="form-check-input"
              type="radio"
              name="frameType"
              id="frame-radio-2"
              value="pattern"
              checked={invoiceData.frame.type === "pattern"}
              onChange={event =>
                updateInvoiceData("frame.type", event.target.value)
              }
            />
            <label className="form-check-label" htmlFor="frame-radio-2">
              Khung viền theo họa tiết
            </label>
          </div>
        </div>

        <BsCollapse isOpen={invoiceData.frame.type === "pattern"}>
          <div>
            <div className="mt-3 row">
              {borderImages.map((url, index) => (
                <div key={index} className="col-3">
                  <div
                    className="card border-image-thumb mb-3"
                    onClick={() => {
                      updateInvoiceData("frame.pattern.url", url);
                    }}
                  >
                    <img src={url} style={{ height: "153px" }} />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label>Độ mờ</Label>
              <Slider
                value={framePatternOpacity}
                min={0}
                max={100}
                orientation="horizontal"
                onChange={value => {
                  setFramePatternOpacity(value);
                  updateInvoiceData("frame.pattern.style", {
                    opacity: `${value / 100}`,
                  });
                }}
              />
            </div>
          </div>
        </BsCollapse>

        <BsCollapse isOpen={invoiceData.frame.type === "border"}>
          <div className="row">
            <div className="col-3">
              <Label>Màu sắc</Label>
              <ColorPicker
                value={invoiceData.frame.border.style.borderColor}
                onChange={color => {
                  updateInvoiceData("frame.border.style", {
                    borderColor: color,
                  });
                }}
              />
            </div>
            <div className="col-9">
              <div className="row">
                <div className="col-6">
                  <Label>Loại viền</Label>
                  <Select
                    value={invoiceData.frame.border.style.borderStyle}
                    onChange={option => {
                      updateInvoiceData("frame.border.style", {
                        borderStyle: option.value,
                      });
                    }}
                    options={borderStyleOptions}
                    components={{
                      Option: BorderStyleOption,
                    }}
                    classNamePrefix="select2-selection"
                  />
                </div>
                <div className="col-6">
                  <Label>Độ dày</Label>
                  <Select
                    value={invoiceData.frame.border.style.borderWidth}
                    onChange={option => {
                      updateInvoiceData("frame.border.style", {
                        borderWidth: option.value,
                      });
                    }}
                    options={borderWidthOptions}
                    classNamePrefix="select2-selection"
                  />
                </div>
              </div>
            </div>
          </div>
        </BsCollapse>

        <div className="d-flex align-items-center justify-content-between mt-3">
          <Label className="flex-grow-1 flex-shrink-0">
            Khoảng cách khung viền với nội dung:
          </Label>
          <div className="ms-4" style={{ width: "151px" }}>
            <div className="input-group mb-3">
              <input
                type="number"
                className="form-control"
                value={invoiceData.frame.padding}
                onChange={event =>
                  updateInvoiceData("frame.padding", event.target.value)
                }
              />
              <span className="input-group-text">px</span>
            </div>
          </div>
        </div>
      </Collapse>
      {/* End frame */}
    </div>
  );
};

export default InvoiceTemplateImageEditor;
